import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown16,
  IconFormIcons,
  IconFormNames,
  Inbox,
  Repeat,
  Search,
  Tag as TagIcon,
  TodoList216,
} from 'shared/assets/icons';
import { InboxCount } from 'shared/components/connected/inbox-count';
import { TagCount } from 'shared/components/connected/navigation-tags-list';
import { SmartListCount } from 'shared/components/connected/smart-lists';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';
import { SmartList } from 'shared/types/smart-list';
import { Tag } from 'shared/types/tag';
import { getAutoCompleteSuggestions } from 'shared/utils/autocomplete-value-matches-input';

import { DefaultSideOptions } from '../../types/side-panel';
import * as Styled from './side-panel.style';

export type SidePanelViewSelectorProps = {
  tags: Tag[];
  smartLists: SmartList[];
  value: DefaultSideOptions | ID;
  onChange: (value: DefaultSideOptions | ID) => void;
};

export type SidePanelViewSelectorHandle = {
  open: () => void;
};

export const SidePanelViewSelector = React.forwardRef<
  SidePanelViewSelectorHandle,
  SidePanelViewSelectorProps
>(({ tags, smartLists, value, onChange }, ref) => {
  const { t } = useTranslation();
  const titleButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());

  React.useImperativeHandle(ref, () => ({
    open: openMenu,
  }));

  /* O(1) lookup maps */
  const tagMap = useMemo(
    () => new Map(tags.map((tag) => [tag.id, tag])),
    [tags],
  );
  const smartListMap = useMemo(
    () => new Map(smartLists.map((sl) => [sl.id, sl])),
    [smartLists],
  );

  /* Static options definition for search and mapping */
  const staticOptions = useMemo(
    () => [
      {
        id: DefaultSideOptions.Inbox,
        name: t('general.menu.navigation.todo.inbox'),
        icon: Inbox,
      },
      {
        id: DefaultSideOptions.AllTasks,
        name: t('general.menu.navigation.todo.all-tasks'),
        icon: TodoList216,
      },
      {
        id: DefaultSideOptions.Habits,
        name: t('general.menu.navigation.my-life.habits'),
        icon: Repeat,
      },
    ],
    [t],
  );

  const staticMap = useMemo(
    () => new Map(staticOptions.map((option) => [option.id, option])),
    [staticOptions],
  );

  /* Unified search results */
  const { isSearching, searchResults, filteredTags, filteredSmartLists } =
    useMemo(() => {
      if (!searchValue) {
        return {
          isSearching: false,
          searchResults: [],
          filteredTags: tags,
          filteredSmartLists: smartLists,
        };
      }

      const matchedStatic = getAutoCompleteSuggestions(
        searchValue,
        staticOptions,
        ['name'],
      );
      const matchedTags = getAutoCompleteSuggestions(searchValue, tags, [
        'name',
      ]);
      const matchedSmartLists = getAutoCompleteSuggestions(
        searchValue,
        smartLists,
        ['name'],
      );

      return {
        isSearching: true,
        searchResults: [...matchedStatic, ...matchedSmartLists, ...matchedTags],
        filteredTags: matchedTags,
        filteredSmartLists: matchedSmartLists,
      };
    }, [searchValue, tags, smartLists, staticOptions]);

  const selectedLabel = useMemo(
    () =>
      staticMap.get(value as DefaultSideOptions)?.name ??
      smartListMap.get(value as string)?.name ??
      tagMap.get(value as string)?.name ??
      '',
    [value, staticMap, smartListMap, tagMap],
  );

  const allKeys = useMemo(() => {
    if (isSearching) {
      return searchResults.map((item) => item.id);
    }
    return [
      DefaultSideOptions.Inbox,
      DefaultSideOptions.AllTasks,
      ...filteredSmartLists.map(({ id }) => id),
      ...filteredTags.map(({ id }) => id),
    ];
  }, [isSearching, searchResults, filteredTags, filteredSmartLists]);

  const focusOption = useCallback(
    (key: string) => optionRefs.current.get(key)?.focus(),
    [],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentIndex = allKeys.indexOf(value);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (allKeys.length) {
          if (currentIndex !== -1 && currentIndex < allKeys.length - 1) {
            focusOption(allKeys[currentIndex + 1]);
            return;
          }
          focusOption(allKeys[0]);
        }
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (allKeys.length) {
          if (currentIndex > 0) {
            focusOption(allKeys[currentIndex - 1]);
            return;
          }
          focusOption(allKeys[allKeys.length - 1]);
        }
      }
    },
    [allKeys, value, focusOption],
  );

  /* Single click handler — reads id from data-value attribute */
  const handleOptionClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const id = e.currentTarget.dataset.value as DefaultSideOptions | ID;
      if (id) {
        onChange(id);
        closeMenu();
      }
    },
    [onChange, closeMenu],
  );

  /* Single keydown handler for option buttons */
  const handleOptionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const key = e.currentTarget.dataset.value;
      if (!key) {
        return;
      }

      const index = allKeys.indexOf(key);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (index < allKeys.length - 1) {
          focusOption(allKeys[index + 1]);
        }
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (index > 0) {
          focusOption(allKeys[index - 1]);
          return;
        }
        inputRef.current?.focus();
      }
    },
    [allKeys, focusOption],
  );

  /* Single ref callback — reads id from data-value attribute */
  const handleOptionRef = useCallback(
    (el: HTMLButtonElement | null) =>
      el?.dataset.value && optionRefs.current.set(el.dataset.value, el),
    [],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value),
    [],
  );

  React.useEffect(() => {
    if (!menuOpen) {
      setSearchValue('');
      return;
    }

    setTimeout(() => inputRef.current?.focus(), 50);
  }, [menuOpen]);

  return (
    <Styled.SelectorWrapper ref={containerRef}>
      <Styled.ClickableTitle
        ref={titleButtonRef}
        onClick={openMenu}
        $isActive={menuOpen}
      >
        <Styled.CustomSelectValue>{selectedLabel}</Styled.CustomSelectValue>
        <Styled.IconContainer>
          <Icon icon={ChevronDown16} />
        </Styled.IconContainer>
      </Styled.ClickableTitle>

      {menuOpen && (
        <PopupMenu
          referenceElement={titleButtonRef}
          onOutside={closeMenu}
          position="bottom"
          width={256}
        >
          <PopupMenuList hasBottomBorder>
            <PopupMenuListItem>
              <PopupMenuInput
                ref={inputRef}
                startIcon={Search}
                value={searchValue}
                placeholder={t('general.labels.search')}
                onChange={handleSearchChange}
                onKeyDown={handleInputKeyDown}
              />
            </PopupMenuListItem>
          </PopupMenuList>

          {isSearching ? (
            /* FLAT SEARCH LIST */
            <PopupMenuList>
              {!searchResults.length && (
                <PopupMenuListItem>
                  <Styled.NoResultsContainer>
                    <Typography variant="body2" color="text.secondary">
                      {t('general.labels.search.no-results')}
                    </Typography>
                  </Styled.NoResultsContainer>
                </PopupMenuListItem>
              )}
              {searchResults.map((item) => {
                const tagItem = tagMap.get(item.id as string);
                const smartListItem = smartListMap.get(item.id as string);
                const staticItem = staticMap.get(item.id as DefaultSideOptions);

                return (
                  <PopupMenuListItem key={item.id}>
                    <PopupMenuButton
                      ref={handleOptionRef}
                      start={
                        tagItem ? (
                          <Styled.IconContainer $color={tagItem.color}>
                            <Icon icon={TagIcon} />
                          </Styled.IconContainer>
                        ) : smartListItem ? (
                          <Styled.IconContainer $color={smartListItem.color}>
                            <Icon
                              icon={
                                IconFormIcons[
                                  smartListItem.icon || IconFormNames.SmartList
                                ]
                              }
                            />
                          </Styled.IconContainer>
                        ) : (
                          <Icon icon={staticItem!.icon} />
                        )
                      }
                      end={
                        tagItem ? (
                          <TagCount tagId={tagItem.id} />
                        ) : smartListItem ? (
                          <SmartListCount smartList={smartListItem} />
                        ) : staticItem?.id === DefaultSideOptions.Inbox ? (
                          <InboxCount />
                        ) : undefined
                      }
                      onClick={handleOptionClick}
                      onKeyDown={handleOptionKeyDown}
                      data-value={item.id}
                      isSelected={value === item.id}
                      overflowEllipsis
                    >
                      {item.name}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                );
              })}
            </PopupMenuList>
          ) : (
            /* DEFAULT VIEW (Grouped) */
            <>
              {staticOptions.map((opt) => (
                <PopupMenuList hasBottomBorder key={opt.id}>
                  <PopupMenuListItem>
                    <PopupMenuButton
                      ref={handleOptionRef}
                      start={<Icon icon={opt.icon} />}
                      end={
                        opt.id === DefaultSideOptions.Inbox ? (
                          <InboxCount />
                        ) : undefined
                      }
                      onClick={handleOptionClick}
                      onKeyDown={handleOptionKeyDown}
                      data-value={opt.id}
                      isSelected={value === opt.id}
                    >
                      {opt.name}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                </PopupMenuList>
              ))}

              <PopupMenuList hasBottomBorder>
                {filteredSmartLists.map((smartList) => (
                  <PopupMenuListItem key={smartList.id}>
                    <PopupMenuButton
                      ref={handleOptionRef}
                      start={
                        <Styled.IconContainer $color={smartList.color}>
                          <Icon
                            icon={
                              IconFormIcons[
                                smartList.icon || IconFormNames.SmartList
                              ]
                            }
                          />
                        </Styled.IconContainer>
                      }
                      end={<SmartListCount smartList={smartList} />}
                      onClick={handleOptionClick}
                      onKeyDown={handleOptionKeyDown}
                      data-value={smartList.id}
                      isSelected={value === smartList.id}
                      overflowEllipsis
                    >
                      {smartList.name}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                ))}
              </PopupMenuList>

              <PopupMenuList>
                {filteredTags.map((tag) => (
                  <PopupMenuListItem key={tag.id}>
                    <PopupMenuButton
                      ref={handleOptionRef}
                      start={
                        <Styled.IconContainer $color={tag.color}>
                          <Icon icon={TagIcon} />
                        </Styled.IconContainer>
                      }
                      end={<TagCount tagId={tag.id} />}
                      onClick={handleOptionClick}
                      onKeyDown={handleOptionKeyDown}
                      data-value={tag.id}
                      isSelected={value === tag.id}
                      overflowEllipsis
                    >
                      {tag.name}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                ))}
              </PopupMenuList>
            </>
          )}
        </PopupMenu>
      )}
    </Styled.SelectorWrapper>
  );
});
